import jitsu from '../jitsu'

import { STIMULUS_ATTRIBUTES, getStimulusValueAttr } from '../../api/componentGenerators/helpers'
import { CONTROLLERS } from '../../api/helpers/controllers'

import type {
  Flow,
  UserData,
  FlowPage,
  Component,
  ComponentContext,
  PageContext,
  ElementContext,
  ResetUserDataOptions,
} from '../../api/models/types'
import type PageController from '../../controllers/page_controller'
import type CustomButtonController from '../../controllers/components/custom_button_controller'
import type OptionSelectorController from '../../controllers/components/option_selector_controller'

interface Global {
  id?: string
}

/**
 * Sets a reactive watcher on userData on the main flow controller.
 * @param {*} self The controller instance this applies to
 * @param {*} cb an optional callback to be called when userData changes, otherwise the function will automatically use userDataValueChanged if it exists
 * @returns a disconnect function for use when the instance is destroyed
 */
// deno-lint-ignore no-explicit-any
export function connectUserData(self: any, cb?: Function, isChildFlow = false) {
  const flow = isChildFlow
    ? getComponentContext(self).flowEl.closest('.Flow-EntireFlow[data-controller*="flow"]')
    : self.element.closest(
        `[data-controller*="flow"]:not([data-controller*="components--child-flow"])`
      )

  const initialValue =
    self.application.getControllerForElementAndIdentifier(flow, 'flow')?.userDataValue || {}

  const initialUserData = isChildFlow ? getChildFlowUserData(self, initialValue) : initialValue
  self.userDataValue = initialUserData

  if (typeof cb === 'function') {
    cb.call(self, initialUserData, {})
  } else if (typeof self.userDataValueChanged === 'function') {
    self.userDataValueChanged.call(self, initialUserData, {})
  }
  const handleEvent = ((
    e: CustomEvent<{
      oldUserData: object
      newUserData: object
    }>
  ) => {
    const updatedUserData = isChildFlow
      ? getChildFlowUserData(self, e.detail.newUserData)
      : e.detail.newUserData

    self.userDataValue = updatedUserData

    if (typeof cb === 'function') cb.call(self, updatedUserData, e.detail.oldUserData)
    else if (typeof self.userDataValueChanged === 'function') {
      self.userDataValueChanged.call(self, updatedUserData, e.detail.oldUserData)
    }
  }) as EventListener
  document.addEventListener(
    `updateFlowUserData-${flow.getAttribute('data-flow-runtime-id-value')}`,
    handleEvent
  )
  return () => {
    document.removeEventListener(
      `updateFlowUserData-${flow.getAttribute('data-flow-runtime-id-value')}`,
      handleEvent
    )
  }
}

export function updateFlowUserData(self: any, newData: Record<string, unknown>) {
  const oldUserData = self.userDataValue
  self.userDataValue = {
    ...newData,
  }
  const event = new CustomEvent(`updateFlowUserData-${self.runtimeIdValue}`, {
    detail: {
      oldUserData,
      newUserData: self.userDataValue,
    },
    bubbles: true,
  })

  document.dispatchEvent(event)
}

interface Config {
  attributeName?: string
  outputValue?: string
}

/**
 * Sets a reactive watcher on a property on the main flow controller.
 * @param {*} self The controller instance
 * @param {*} config An optional object containing 'attributeName' or 'outputValue'. 'attributeName' is the attribute to watch on the main flow controller, and 'outputValue' is the property on the controller to write to
 * @param {*} cb An optional callback to run when the watched property changes.
 * @returns a disconnect function to use when the controller is destroyed
 */
// deno-lint-ignore no-explicit-any
export function connectFlowValue(self: any, config: Config, cb?: Function) {
  const names = typeof config === 'object' ? config : {}
  const { attributeName, outputValue } = names

  const flow = self.element.closest(`[data-controller*="flow"]`)
  if (outputValue && attributeName)
    self[outputValue] = typecastValue(flow?.getAttribute(attributeName))
  if (typeof cb === 'function' && attributeName)
    cb.call(self, typecastValue(flow?.getAttribute(attributeName)), undefined)
  if (MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      for (let mutation of mutations) {
        if (mutation.type === 'attributes' && attributeName) {
          const attributeValue = typecastValue(
            (mutation.target as HTMLElement).getAttribute(attributeName)
          )
          const oldAttributeValue = typecastValue(mutation.oldValue)
          if (outputValue) self[outputValue] = attributeValue
          if (typeof cb === 'function') cb.call(self, attributeValue, oldAttributeValue)
        }
      }
    })
    if (attributeName)
      observer.observe(flow as Element, {
        attributeFilter: [attributeName],
        attributeOldValue: true,
      })
    return () => observer.disconnect()
  } else {
    console.warn('Warning: Mutation Observer is not supported')
  }
}

/**
 * Sets a reactive page requirement property as isValidValue on the controller that updates when a page's validation state changes.
 * @param {*} self The controller instance
 */
// deno-lint-ignore no-explicit-any
export function connectPageRequirements(self: any, cb?: Function) {
  const flow = self.element.closest(`[data-controller*="flow"]`)
  const flowController = self.application.getControllerForElementAndIdentifier(flow, 'flow')

  const attributeName = 'data-flow-requirements-met-value'
  const initialValue = flow?.getAttribute(attributeName) === 'true'

  if (typeof cb === 'function') cb.call(self, initialValue, undefined)
  else if (typeof self.isValidValueChanged === 'function') {
    // @TODO: Maybe replace {} with false/undefined?
    self.isValidValueChanged.call(self, initialValue, {})
  }

  if (MutationObserver) {
    const observer = new MutationObserver((mutations) => {
      for (let mutation of mutations) {
        if (mutation.type === 'attributes') {
          const attributeValue =
            (mutation.target as HTMLElement).getAttribute(attributeName) === 'true'
          const oldAttributeValue = mutation.oldValue === 'true'
          self.isValidValue = attributeValue
          if (typeof cb === 'function') cb.call(self, attributeValue, oldAttributeValue)
          else if (typeof self.isValidValueChanged === 'function') {
            self.isValidValueChanged.call(self, attributeValue, oldAttributeValue)
          }
        }
      }
    })
    observer.observe(flow as Element, { attributeFilter: [attributeName], attributeOldValue: true })
    self.isValidValue = initialValue
    return () => observer.disconnect()
  } else {
    self.element.setAttribute('data-requirements-met', 'true')
    self.isValidValue = Boolean(flowController.getPageValidationStatus())
  }
}

/**
 * Fetches the parsed Flow object from the main flow controller.
 * @param {*} self The controller instance
 * @param {*} raw optional argument to return the raw flow object without any conditions applied.
 * @returns The parsed flow JSON that is currently loaded
 */
// deno-lint-ignore no-explicit-any
export function getFlow(self: any, raw?: boolean): Flow {
  const flow = self.element.closest(`[data-controller~="flow"]`)
  const otherController = self.application.getControllerForElementAndIdentifier(flow, 'flow')
  return otherController?.getFlow(raw)
}

export function setFlow(self: any, raw: any) {
  const flow = self.element.closest(`[data-controller~="flow"]`)
  const otherController = self.application.getControllerForElementAndIdentifier(flow, 'flow')
  return otherController.setFlow(raw)
}

export function watchFlowUpdates(self: any, callback: Function): Function {
  const flow = self.element.closest(`[data-controller~="flow"]`)
  const otherController = self.application.getControllerForElementAndIdentifier(flow, 'flow')
  return otherController.watchFlowUpdates(callback)
}

/**
 * Returns the page controller's open info box function. Note that this will return an empty function during the flow controller's connect function
 * @param {*} self The controller instance
 * @returns A function to invoke to open an info box
 */
export async function getOpenInfoBoxFn(self: any) {
  // 'self' is always a Flow element, so can just look inside it for the page
  const page = self.element.querySelector(`[data-controller*="page"]`)
  // It is possible that the page controller has not been loaded yet, wrapping in an empty Promise.resolve() ensures we only
  // run this code after the controller is loaded. Refer to https://github.com/hotwired/stimulus/issues/201 for more details.
  const otherController = await Promise.resolve().then(() =>
    self.application.getControllerForElementAndIdentifier(page, 'page')
  )
  return otherController
    ? otherController.openInfoBox
    : () => console.log('Open Info Box: No Page Controller found')
}

export async function getCloseInfoBoxFn(self: any) {
  const page = self.element.querySelector(`[data-controller*="page"]`)
  const otherController = await Promise.resolve().then(() =>
    self.application.getControllerForElementAndIdentifier(page, 'page')
  )
  return otherController
    ? otherController.closeInfoBox
    : () => console.log('Close Info Box: No Page Controller found')
}

/**
 * Returns a function to set user data, one key at a time. Used for actions and computed fields
 * @param {*} self The controller instance
 * @returns The setUserData function that accepts argument 'key' and 'value'
 */
export function getSetUserDataFn(self: any) {
  const flow = self.element.closest(`[data-controller*="flow"]`)
  const otherController = self.application.getControllerForElementAndIdentifier(flow, 'flow')
  return (key: string, value: string, immediate?: boolean) => {
    const flowNow = self.element.closest(`[data-controller*="flow"]`)
    const otherControllerNow = self.application.getControllerForElementAndIdentifier(
      flowNow,
      'flow'
    )
    if (!otherControllerNow) {
      console.warn('Flow no longer exists, cannot set user data')
      return
    }

    const pages = getFlow(self, true)?.pages || []

    switch (key) {
      case 'current_page_key': {
        const pageId = pages.find((p: FlowPage) => p.key === value)?.id
        otherController.pageIdValue = pageId
        // otherController.setUserData('current_page_id', pageId)
        // self.dispatch('update', { detail: { key: 'current_page_id', value: pageId } })
        break
      }

      case 'current_page_index': {
        const pageId = pages[value as any]?.id
        otherController.pageIdValue = pageId
        // otherController.setUserData('current_page_id', pageId)
        // self.dispatch('update', { detail: { key: 'current_page_id', value: pageId } })
        break
      }

      case 'current_page_id': {
        otherController.pageIdValue = value
        break
      }

      default: {
        otherController.setUserData(key, value, immediate)
        // self.dispatch('update', { detail: { key, value } })
        break
      }
    }
  }
  // return otherController.setUserData
}

/**
 * Returns a function to get user data
 * @param {*} self The controller instance
 * @returns A function to get the current userDataValue
 */
export function getUserDataFn(self: any) {
  return () => {
    return self.userDataValue as UserData
  }
}

/**
 * Returns the flow controller's reset function
 * @param {*} self The controller instance
 * @returns A function to reset user data
 */
export function getResetUserDataFn(self: any) {
  const flow = self.element.closest(`[data-controller*="flow"]`)
  const flowController = self.application.getControllerForElementAndIdentifier(flow, 'flow')
  return (options?: ResetUserDataOptions) => {
    const flowNow = self.element.closest(`[data-controller*="flow"]`)
    const otherControllerNow = self.application.getControllerForElementAndIdentifier(
      flowNow,
      'flow'
    )
    if (!otherControllerNow) {
      console.warn('Flow no longer exists, cannot reset user data')
      return
    }

    return flowController.reset(options)
  }
}

/**
 * Returns a function to dispatch a single user data update. Recommended for component controllers.
 * @param {*} self The controller instance
 * @returns function(key, value)
 */
export function getDispatchUpdateFn(self: any) {
  const pages = getFlow(self, true)?.pages || []
  const mapvalues = (key: string, value: any) => {
    switch (key) {
      case 'current_page_key': {
        const pageId = pages.find((p: FlowPage) => p.key === value)?.id
        self.dispatch('to-page', { detail: { value: pageId } })
        return []
      }

      case 'current_page_index': {
        const pageId = pages[value]?.id
        self.dispatch('to-page', { detail: { value: pageId } })
        return []
      }

      case 'current_page_id': {
        self.dispatch('to-page', { detail: { value } })
        return []
      }

      default: {
        return [key, value]
      }
    }
  }
  return (k: object, v: string) => {
    if (typeof k === 'object') {
      const updates = {}
      Object.entries(k).forEach(([ky, val]) => {
        const [key, value] = mapvalues(ky, val)
        if (key) (updates as Record<string, unknown>)[key] = value
      })
      self.dispatch('update', { detail: { updates } })
    } else {
      const [key, value] = mapvalues(k, v)
      self.dispatch('update', { detail: { key, value } })
    }
  }
}

/**
 * Trigger an action, based on id.
 * @param {*} self The controller instance.
 * @param {*} key The id of the action to be triggered
 * @returns A promise that resolves when the action has been successfully executed.
 */
export function dispatchAction(
  self: any,
  key:
    | string
    | { detail: { actions: Record<string, unknown>[] | string[] } }
    | Record<string, unknown>[]
) {
  const flow = self.element.closest(`[data-controller*="flow"]`)
  const otherController = self.application.getControllerForElementAndIdentifier(
    flow || self.flowEl,
    'flow'
  )
  return otherController.dispatchAction(key)
}

export function triggerFormValidation(self: any) {
  const flow = self.element.closest(`[data-controller*="flow"]`)
  const otherController = self.application.getControllerForElementAndIdentifier(flow, 'flow')
  return otherController.triggerFormValidation()
}

/**
 * Gets the component element based on an element
 * Could return the same element, or a parent if the provided element is inside (i.e. part of) a component
 * @param {*} el The provided element
 * @returns A component element
 */
export function getNearestComponentElement(el: any) {
  return el.closest('[data-component-type-value]')
}

/**
 * Returns the context of a given component controller.
 * @param {*} self The controller instance.
 * @returns An object with the shape: { flowId, component, flowVersion, componentId, flowEl, pageEl, pageId }
 */
// deno-lint-ignore no-explicit-any
export function getComponentContext(self: any): ComponentContext {
  const flowEl = self.element.closest(`[data-controller*="flow"]`)
  const flowId = flowEl?.getAttribute('data-flow-id-value')
  const pageEl = flowEl?.querySelector(`[data-controller~="page"]`)
  const pageId = self.element
    .closest('[data-component-page-id-value]')
    .getAttribute('data-component-page-id-value')
  const flowVersion = pageEl?.getAttribute('data-page-version-value')
  const branchId = pageEl?.getAttribute('data-page-branch-id-value')
  // Gets the last one after splitting by '>', to cater for repeatable components
  const componentId =
    (
      self.element.closest('[data-component-id-value]').getAttribute('data-component-id-value') ||
      ''
    )
      .split('--')
      .slice(-1)[0] || null
  const component = getComponent(self, pageId, componentId) as Component
  return { flowId, component, flowVersion, componentId, flowEl, pageEl, pageId, branchId }
}

export function getPageContext(self: any): PageContext {
  const flowEl = self.element.closest(`[data-controller*="flow"]`)
  const flowId = flowEl?.getAttribute('data-flow-id-value')
  const pageEl = flowEl?.querySelector(`[data-controller~="page"]`)
  const flowVersion = pageEl?.getAttribute('data-page-version-value')
  const pageId = pageEl?.getAttribute('data-page-page-id-value')
  const branchId = flowEl?.getAttribute('data-flow-branch-id-value')
  const page = getPage(self, pageId) as FlowPage
  return { flowId, flowEl, flowVersion, page, pageId, pageEl, branchId }
}

export function getElementContext(self: any): ElementContext {
  let isPage = false
  if (self.element.classList.contains('Flow-Page')) isPage = true
  const context = isPage ? getPageContext(self) : getComponentContext(self)
  return { isPage, ...context }
}

// deno-lint-ignore no-explicit-any
export function getPageNavHandlers(self: any) {
  const goToNextPage = () => {
    const event = {
      detail: { value: self.pageId || self.pageIdValue },
    }
    if (self.next) {
      self.next(event)
      return
    }
    if (self.dispatch) {
      self.dispatch('next', event)
      return
    }
  }

  const goToPrevPage = () => {
    if (self.prev) {
      self.prev()
      return
    }
    if (self.dispatch) {
      self.dispatch('prev')
      return
    }
  }

  const goToPage = (page: { key?: string; id?: string; index?: number; idOrKey?: string }) => {
    const pages = page.key || page.idOrKey ? getFlow(self, true)?.pages || [] : []
    const pageIdFromIdOrKey =
      page.idOrKey &&
      (pages.find((pg) => page.idOrKey === pg.id)?.id ||
        pages.find((pg) => page.idOrKey === pg.key)?.id)
    const pageIdFromKey = page.key && pages.find((pg) => page.key === pg.key)?.id
    const extractedId = pageIdFromIdOrKey || pageIdFromKey || page.id

    const event = {
      detail: { value: extractedId || page.index },
    }
    if (self.goToPage) {
      self.goToPage(event)
      return true
    }
    if (self.dispatch) {
      self.dispatch('to-page', event)
      return true
    }
  }

  return {
    goToNextPage,
    goToPrevPage,
    goToPage,
  }
}

// deno-lint-ignore no-explicit-any
export function lifecycleTrigger(self: any, state: 'mounted' | 'unmounted') {
  let actions = []
  switch (state) {
    case 'mounted':
      actions = self?.component?.outputs_onmounted
      break
    case 'unmounted':
      actions = self?.component?.outputs_onunmounted
      break
  }
  Array.isArray(actions)
    ? dispatchAction(self, {
        detail: { actions: actions },
      })
    : ''
}

export function trackCustomEvent(
  flowId: string,
  customEventName: string,
  customEventProps?: Record<string, unknown>
) {
  return jitsu.track('custom_event:triggered', {
    flow_id: flowId,
    custom_event_name: customEventName,
    custom_event_props: JSON.stringify(customEventProps || {}),
  })
}

export function doNotStoreComponentDataInCloud(component_json: any): boolean {
  if (!component_json || typeof component_json !== 'object') return false
  return Boolean(component_json.doNotSave)
}

// deno-lint-ignore no-explicit-any
function getComponent(self: any, pageId: string, componentId: string) {
  const flow = getFlow(self, true)
  const globals = flow?.components || []
  const global = globals.find((g: Global) => g.id === componentId)
  if (global) return global

  // const pages = flow.pagesById || {}
  // const page = pages[pageId]
  const pages = flow?.pages || []
  const page = pages.find((p: FlowPage) => p.id === pageId)
  if (page) {
    const pageComponents = page.components || []
    const component = pageComponents.find((c: Component) => c.id === componentId)
    if (component) return component
  }
  return null
}

function getPage(self: any, pageId: string) {
  const flow = getFlow(self, true)
  const pages = flow?.pages || []
  const page = pages.find((p: FlowPage) => p.id === pageId)
  return page
}

function typecastValue(value: string | undefined | null) {
  if (value === 'false') return false
  if (value === 'true') return true
  if (value && !isNaN(+value)) return +value

  try {
    if (value) {
      const parsed = JSON.parse(value)
      return parsed
    }
  } catch (error) {
    return value
  }
}

const flowDisableInteractionsDuringTransitionsAttr = getStimulusValueAttr({
  controller: CONTROLLERS.flow,
  key: STIMULUS_ATTRIBUTES[CONTROLLERS.flow].disableAllInteractionsDuringTransitions,
})

export function shouldDisableInteractionDuringTransition(
  self: CustomButtonController | OptionSelectorController
): boolean {
  const flowEl = self.flowEl
  if (!isDisableAllInteractionsDuringTransitionsEnabled({ flowEl })) return false

  //* Get the page element
  const pageEl = flowEl?.querySelector('[data-controller~="page"]')
  if (!pageEl) return false

  //* Get the page controller
  const pageController = self.application.getControllerForElementAndIdentifier(
    pageEl,
    'page'
  ) as PageController | null

  //* Check if the page is in transition
  return pageController?.isTransitionInProgress() ?? false
}

function isDisableAllInteractionsDuringTransitionsEnabled({
  flowEl,
}: {
  flowEl: Element | null | undefined
}): boolean {
  return flowEl?.getAttribute(flowDisableInteractionsDuringTransitionsAttr) === 'true'
}

/**
 * For a child flow, we want to update only those fields that are bound to
 * the parent flow through the `parent_field_bindings` attribute.
 */
// deno-lint-ignore no-explicit-any
function getChildFlowUserData(self: any, userData: object) {
  const dataFromParent: { [key: string]: any } = {}
  self.component?.parent_field_bindings?.map((key: string) => {
    dataFromParent[key] = (userData as { [key: string]: any })[key]
  })
  return { ...self.userDataValue, ...dataFromParent }
}
